import React, { useState, useMemo } from 'react';
import {
    Search,
    ChevronLeft,
    ChevronRight,
    MoreVertical,
    Loader2,
    Inbox
} from 'lucide-react';

const DataTable1 = ({
    data = [],
    columns = [],
    loading = false,
    enableSearch = false,
    enablePagination = false,
    rowsPerPage = 10,
    rowActions = [],
    onAction = () => { },
    emptyMessage = "No records found"
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [actionMenuIndex, setActionMenuIndex] = useState(null);

    const filteredData = useMemo(() => {
        if (!enableSearch || !searchTerm) return data;
        return data.filter(row =>
            Object.values(row).some(val =>
                String(val).toLowerCase().includes(searchTerm.toLowerCase())
            )
        );
    }, [data, searchTerm, enableSearch]);

    const totalPages = Math.ceil(filteredData.length / rowsPerPage);
    const paginatedData = useMemo(() => {
        if (!enablePagination) return filteredData;
        const start = (currentPage - 1) * rowsPerPage;
        return filteredData.slice(start, start + rowsPerPage);
    }, [filteredData, currentPage, rowsPerPage, enablePagination]);

    return (
        <div className="w-full bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            {/* Header / Search Bar - Responsive Padding */}
            {enableSearch && (
                <div className="p-3 sm:p-4 border-b border-gray-50 bg-gray-50/50">
                    <div className="relative w-full sm:max-w-xs md:max-w-sm">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                        <input
                            type="text"
                            placeholder="Search records..."
                            className="w-full pl-10 pr-4 py-2 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 transition-all text-sm"
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setCurrentPage(1);
                            }}
                        />
                    </div>
                </div>
            )}

            {/* Loading State */}
            {loading && (
                <div className="py-20 text-center">
                    <Loader2 className="mx-auto animate-spin text-purple-600 mb-2" size={32} />
                    <span className="text-gray-500 text-sm font-medium">Fetching records...</span>
                </div>
            )}

            {/* Empty State */}
            {!loading && paginatedData.length === 0 && (
                <div className="py-20 text-center text-gray-400 px-4">
                    <Inbox className="mx-auto mb-2 opacity-20" size={48} />
                    <p className="text-sm">{emptyMessage}</p>
                </div>
            )}

            {/* Mobile/Tablet Card View (Hidden on large desktops) */}
            {!loading && paginatedData.length > 0 && (
                <div className="block lg:hidden">
                    <div className="divide-y divide-gray-100">
                        {paginatedData.map((row, rowIndex) => (
                            <div key={rowIndex} className="p-4 bg-white active:bg-gray-50 transition-colors relative">
                                {/* Actions for Mobile */}
                                {rowActions.length > 0 && (
                                    <div className="absolute right-2 top-2">
                                        <button
                                            onClick={() => setActionMenuIndex(actionMenuIndex === rowIndex ? null : rowIndex)}
                                            className="p-2 text-gray-400"
                                        >
                                            <MoreVertical size={20} />
                                        </button>
                                        {actionMenuIndex === rowIndex && (
                                            <>
                                                <div className="fixed inset-0 z-10" onClick={() => setActionMenuIndex(null)} />
                                                <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-100 shadow-2xl rounded-lg z-20 py-1">
                                                    {rowActions.map((action) => (
                                                        <button
                                                            key={action.key}
                                                            onClick={() => {
                                                                onAction(action.key, row);
                                                                setActionMenuIndex(null);
                                                            }}
                                                            className="w-full text-left px-4 py-3 text-sm text-gray-700 active:bg-purple-50"
                                                        >
                                                            {action.label}
                                                        </button>
                                                    ))}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                )}

                                {/* Card Content Mapping */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-4">
                                    {columns.map((col, idx) => (
                                        <div key={col.key} className={`${idx === 0 ? 'sm:col-span-2 mb-1' : ''}`}>
                                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-tight leading-none mb-1">
                                                {col.label}
                                            </p>
                                            <div className="text-sm text-gray-700">
                                                {col.render ? col.render(row[col.key], row) : row[col.key]}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Desktop Table View (Hidden on Mobile/Tablet) */}
            {!loading && paginatedData.length > 0 && (
                <div className="hidden lg:block overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-gray-50/80 border-b border-gray-100">
                                {columns.map((col) => (
                                    <th key={col.key} className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                        {col.label}
                                    </th>
                                ))}
                                {rowActions.length > 0 && <th className="px-6 py-4 w-10"></th>}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                            {paginatedData.map((row, rowIndex) => (
                                <tr key={rowIndex} className="hover:bg-gray-50/50 transition-colors group">
                                    {columns.map((col) => (
                                        <td key={col.key} className="px-6 py-4 text-sm text-gray-600">
                                            {col.render ? col.render(row[col.key], row) : row[col.key]}
                                        </td>
                                    ))}
                                    {rowActions.length > 0 && (
                                        <td className="px-6 py-4 text-right relative">
                                            <button
                                                onClick={() => setActionMenuIndex(actionMenuIndex === rowIndex ? null : rowIndex)}
                                                className="p-1 hover:bg-gray-200 rounded-full transition-colors text-gray-400 hover:text-gray-600"
                                            >
                                                <MoreVertical size={18} />
                                            </button>
                                            {actionMenuIndex === rowIndex && (
                                                <>
                                                    <div className="fixed inset-0 z-10" onClick={() => setActionMenuIndex(null)} />
                                                    <div className="absolute right-10 top-4 w-48 bg-white border border-gray-100 shadow-xl rounded-lg z-20 py-1 overflow-hidden">
                                                        {rowActions.map((action) => (
                                                            <button
                                                                key={action.key}
                                                                onClick={() => {
                                                                    onAction(action.key, row);
                                                                    setActionMenuIndex(null);
                                                                }}
                                                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-purple-50 hover:text-purple-700 transition-colors"
                                                            >
                                                                {action.label}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </>
                                            )}
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Pagination Controls - Responsive Layout */}
            {enablePagination && filteredData.length > rowsPerPage && (
                <div className="p-4 border-t border-gray-50 bg-gray-50/30 flex flex-col sm:flex-row items-center justify-between gap-4">
                    <span className="text-xs sm:text-sm text-gray-500 order-2 sm:order-1">
                        Showing <span className="font-medium text-gray-700">{((currentPage - 1) * rowsPerPage) + 1}</span> to <span className="font-medium text-gray-700">{Math.min(currentPage * rowsPerPage, filteredData.length)}</span> of {filteredData.length}
                    </span>
                    <div className="flex gap-2 order-1 sm:order-2 w-full sm:w-auto justify-between sm:justify-end">
                        <button
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                            className="flex-1 sm:flex-none flex items-center justify-center px-4 py-2 sm:p-2 border border-gray-200 rounded-lg bg-white disabled:opacity-40 transition-all shadow-sm active:scale-95"
                        >
                            <ChevronLeft size={18} className="sm:size-4" />
                            <span className="ml-2 sm:hidden text-sm font-medium">Previous</span>
                        </button>
                        <button
                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                            disabled={currentPage === totalPages}
                            className="flex-1 sm:flex-none flex items-center justify-center px-4 py-2 sm:p-2 border border-gray-200 rounded-lg bg-white disabled:opacity-40 transition-all shadow-sm active:scale-95"
                        >
                            <span className="mr-2 sm:hidden text-sm font-medium">Next</span>
                            <ChevronRight size={18} className="sm:size-4" />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DataTable1;